import { useEffect, useState, useMemo } from "react";
import { Save, FileCode2, Eye, Globe2, FileText as FileTextIcon, Loader2, History } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";

import "@/styles/legal.css";

/**
 * Phase 7C+ — Admin tab: Documents légaux.
 *
 * - Variables form (effective_date_label, ville_du_centre, signataire,
 *   référent harcèlement, contact email).
 * - HTML editor for the règlement intérieur (textarea + Aperçu live).
 * - Version history + acknowledgments table.
 */
export default function AdminLegalDocumentsTab() {
    const [settings, setSettings] = useState(null);
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);
    const [versions, setVersions] = useState([]);
    const [acks, setAcks] = useState([]);
    const [bump, setBump] = useState("minor");

    const reload = async () => {
        setLoading(true);
        try {
            const [s, d, v, a] = await Promise.all([
                api.get("/admin/legal-settings"),
                api.get("/admin/legal-documents/reglement_interieur"),
                api.get("/admin/legal-documents/reglement_interieur/versions"),
                api.get("/admin/legal-documents/reglement_interieur/acknowledgments"),
            ]);
            setSettings(s.data);
            setDoc(d.data);
            setVersions(v.data || []);
            setAcks(a.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    const setS = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const setD = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            await api.put("/admin/legal-settings", settings);
            toast.success("Variables légales enregistrées");
            await reload();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSavingSettings(false); }
    };

    const publishNew = async () => {
        if (!window.confirm(
            `Publier une nouvelle version (bump ${bump}) ?\n\n` +
            `• Les acceptations précédentes seront invalidées.\n` +
            `• Tous les utilisateurs devront ré-accepter à leur prochaine connexion.`
        )) return;
        setPublishing(true);
        try {
            const { data } = await api.put("/admin/legal-documents/reglement_interieur", {
                title: doc.title,
                content_html: doc.content_html,
                mandatory_acknowledgment: doc.mandatory_acknowledgment ?? true,
                bump,
            });
            toast.success(`Nouvelle version publiée : v${data.version}`);
            await reload();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setPublishing(false); }
    };

    const openPdf = () => window.open(`${API}/legal/reglement_interieur/pdf`, "_blank");

    const previewHtml = useMemo(() => {
        if (!doc?.content_html || !settings) return "";
        // Lightweight client-side Jinja-like substitution for preview only
        const vars = { ...settings, version: doc.version || "1.0" };
        return doc.content_html.replace(/\{\{\s*([\w]+)\s*(?:\|\s*default\([^)]*\))?\s*\}\}/g,
            (_, k) => (vars[k] !== undefined && vars[k] !== null) ? String(vars[k]) : `{{ ${k} }}`);
    }, [doc, settings]);

    if (loading || !settings || !doc) {
        return (
            <div className="flex items-center justify-center py-20 text-brand-smoke">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Chargement des documents…
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="admin-legal-tab">
            {/* Variables */}
            <section className="rounded-2xl border border-brand-line bg-white p-8 shadow-premium" data-testid="legal-vars-form">
                <div className="flex items-center gap-3">
                    <Globe2 className="h-5 w-5 text-brand-orange" />
                    <h2 className="font-display text-2xl">Variables Jinja du règlement</h2>
                </div>
                <p className="mt-2 text-sm text-brand-smoke">
                    Ces valeurs sont injectées dans le HTML à chaque rendu (page publique, PDF, modal d'acquittement).
                </p>
                <div className="mt-6 grid gap-5 md:grid-cols-2">
                    <Field label="Date d'entrée en vigueur (ISO)" v={settings.effective_date} on={(v) => setS("effective_date", v)} test="legal-effective-date" />
                    <Field label="Date d'entrée en vigueur (libellé affiché)" v={settings.effective_date_label} on={(v) => setS("effective_date_label", v)} test="legal-effective-label" />
                    <Field label="Ville du centre" v={settings.ville_du_centre} on={(v) => setS("ville_du_centre", v)} test="legal-ville" />
                    <Field label="Nom du signataire (directeur)" v={settings.admin_signataire_name} on={(v) => setS("admin_signataire_name", v)} test="legal-signataire" />
                    <Field label="Référent harcèlement — nom" v={settings.referent_harcelement_name} on={(v) => setS("referent_harcelement_name", v)} test="legal-ref-name" />
                    <Field label="Référent harcèlement — email" v={settings.referent_harcelement_email} on={(v) => setS("referent_harcelement_email", v)} type="email" test="legal-ref-email" />
                    <Field label="Email contact général" v={settings.contact_email} on={(v) => setS("contact_email", v)} type="email" test="legal-contact-email" />
                </div>
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={saveSettings} disabled={savingSettings}
                        data-testid="legal-vars-save"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-sm font-semibold text-white"
                    >
                        <Save className="h-3.5 w-3.5" />
                        {savingSettings ? "Enregistrement…" : "Enregistrer les variables"}
                    </button>
                </div>
            </section>

            {/* HTML editor */}
            <section className="rounded-2xl border border-brand-line bg-white p-8 shadow-premium" data-testid="legal-doc-editor">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <FileTextIcon className="h-5 w-5 text-brand-orange" />
                        <h2 className="font-display text-2xl">Règlement intérieur · v{doc.version}</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPreviewMode((v) => !v)}
                            data-testid="legal-toggle-preview"
                            className="inline-flex items-center gap-1.5 rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs hover:border-brand-orange"
                        >
                            {previewMode ? <FileCode2 className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                            {previewMode ? "Voir le HTML" : "Aperçu"}
                        </button>
                        <button
                            onClick={openPdf}
                            data-testid="legal-open-pdf"
                            className="inline-flex items-center gap-1.5 rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs hover:border-brand-orange"
                        >
                            PDF actuel
                        </button>
                    </div>
                </div>

                <div className="mt-4">
                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Titre</label>
                    <input
                        type="text" value={doc.title}
                        onChange={(e) => setD("title", e.target.value)}
                        data-testid="legal-doc-title"
                        className="mt-1 w-full rounded-xl border border-brand-line bg-white px-4 py-2.5 outline-none focus:border-brand-orange"
                    />
                </div>

                <div className="mt-5">
                    <label className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                        <span>{previewMode ? "Aperçu rendu" : "Contenu HTML (Jinja2)"}</span>
                        <span className="font-normal text-[10px] normal-case text-brand-smoke">
                            Variables disponibles : {"{{ effective_date_label }}"}, {"{{ ville_du_centre }}"}, {"{{ admin_signataire_name }}"}, {"{{ referent_harcelement_name }}"}, {"{{ referent_harcelement_email }}"}, {"{{ contact_email }}"}, {"{{ version }}"}.
                        </span>
                    </label>
                    {previewMode ? (
                        <div
                            className="mt-2 max-h-[480px] overflow-y-auto rounded-xl border border-brand-line bg-[#FBF8F3] p-6"
                            data-testid="legal-preview"
                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                    ) : (
                        <textarea
                            rows={20}
                            value={doc.content_html}
                            onChange={(e) => setD("content_html", e.target.value)}
                            data-testid="legal-doc-html"
                            className="mt-2 w-full rounded-xl border border-brand-line bg-[#FAFAFA] px-4 py-3 font-mono text-xs leading-relaxed outline-none focus:border-brand-orange"
                            spellCheck="false"
                        />
                    )}
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-xs">
                        <input
                            type="checkbox" checked={doc.mandatory_acknowledgment ?? true}
                            onChange={(e) => setD("mandatory_acknowledgment", e.target.checked)}
                            data-testid="legal-doc-mandatory"
                            className="h-4 w-4 accent-brand-orange"
                        />
                        Acceptation obligatoire à la connexion (modal bloquant)
                    </label>

                    <div className="flex items-center gap-3">
                        <select
                            value={bump} onChange={(e) => setBump(e.target.value)}
                            data-testid="legal-doc-bump"
                            className="rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs"
                        >
                            <option value="minor">{`Mineur (correction · v${doc.version} → v${nextVersion(doc.version, "minor")})`}</option>
                            <option value="major">{`Majeur (refonte · v${doc.version} → v${nextVersion(doc.version, "major")})`}</option>
                        </select>
                        <button
                            onClick={publishNew} disabled={publishing}
                            data-testid="legal-doc-publish"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c]"
                        >
                            <Save className="h-3.5 w-3.5" />
                            {publishing ? "Publication…" : "Publier nouvelle version"}
                        </button>
                    </div>
                </div>
            </section>

            {/* Versions + Acks */}
            <div className="grid gap-6 md:grid-cols-2">
                <section className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="legal-versions-history">
                    <div className="flex items-center gap-3"><History className="h-4 w-4 text-brand-orange" /><h3 className="font-display text-lg">Historique des versions</h3></div>
                    <ul className="mt-4 space-y-2">
                        {versions.map((v) => (
                            <li key={v.id} className="flex items-center justify-between rounded-lg border border-brand-line p-3 text-sm">
                                <div>
                                    <span className="font-semibold">v{v.version}</span>
                                    <span className="ml-2 text-xs text-brand-smoke">{(v.created_at || "").slice(0, 10)}</span>
                                </div>
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${v.published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                                    {v.published ? "Publié" : "Archivé"}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>

                <section className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="legal-acks-list">
                    <div className="flex items-center justify-between">
                        <h3 className="font-display text-lg">Acceptations enregistrées</h3>
                        <span className="rounded-full bg-brand-orange/10 px-2.5 py-0.5 text-[11px] font-semibold text-brand-orange">{acks.length}</span>
                    </div>
                    <p className="mt-1 text-xs text-brand-smoke">Preuves Qualiopi · horodatées + IP + user-agent.</p>
                    <ul className="mt-3 max-h-[280px] space-y-2 overflow-y-auto">
                        {acks.length === 0 && <li className="py-6 text-center text-xs text-brand-smoke">Aucune acceptation pour le moment.</li>}
                        {acks.map((a) => (
                            <li key={a.id} className="flex items-center justify-between rounded-lg border border-brand-line p-2.5 text-xs">
                                <div className="min-w-0">
                                    <div className="truncate font-semibold">{a.user_email}</div>
                                    <div className="text-brand-smoke">{a.user_role} · v{a.version}</div>
                                </div>
                                <div className="text-right text-[10px] text-brand-smoke">
                                    {(a.accepted_at || "").slice(0, 16).replace("T", " ")}
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            </div>
        </div>
    );
}

function Field({ label, v, on, type = "text", test }) {
    return (
        <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">{label}</span>
            <input
                type={type} value={v || ""} onChange={(e) => on(e.target.value)}
                data-testid={test}
                className="mt-1 w-full rounded-xl border border-brand-line bg-white px-3 py-2 outline-none focus:border-brand-orange"
            />
        </label>
    );
}

function nextVersion(current, bump) {
    if (!current) return "1.0";
    const [maj, min] = current.split(".").map((n) => parseInt(n, 10));
    return bump === "major" ? `${maj + 1}.0` : `${maj}.${min + 1}`;
}
